<template>
	<div
		class="fixed inset-0 z-[1000] flex flex-wrap bg-gray-50 p-5 duration-200 lg:pointer-events-auto lg:relative lg:inset-auto lg:z-0 lg:overflow-x-visible lg:bg-transparent lg:p-0 lg:pl-1 lg:pr-6 lg:opacity-100"
		:class="[
			showMobileFilter ? 'opacity-100' : 'opacity-0',
			showMobileFilter ? '' : 'pointer-events-none',
			showMobileFilter ? 'overflow-y-scroll' : '',
			showMobileFilter ? 'overflow-y-auto' : '',
		]"
	>
		<div class="flex w-full flex-wrap pb-20 lg:pb-0">
			<div
				class="fixed inset-x-0 top-0 z-20 flex w-full flex-nowrap justify-between bg-gray-50 p-5 lg:relative lg:top-auto lg:flex-wrap lg:bg-transparent lg:p-0"
			>
				<div
					class="flex w-full items-center lg:flex-row-reverse lg:items-start lg:justify-between lg:text-gray-700"
				>
					<span>
						<IconFilter
							class="mr-3 hidden h-5 w-5 lg:mr-0 lg:h-4 lg:w-4"
						/>
					</span>
					<span
						class="lg:text-mlg font-heading text-2xl font-bold leading-none text-gray-700 lg:text-gray-700"
					>
						Filter
					</span>
				</div>

				<hr class="mt-4 hidden w-full lg:flex" />

				<span
					class="flex cursor-pointer items-center hover:text-blue-400 lg:hidden"
					@click="closeMobileFilter"
				>
					<IconTimes class="h-6 w-6 text-gray-700" />
				</span>
			</div>

			<div
				class="mt-14 flex w-full flex-col space-y-6 pb-40 lg:mt-6 lg:pb-0"
			>
				<template v-if="isSubscription">
					<StateFacet />

					<PriceFacet />

					<MakeModelFacet />

					<CheckboxFacet facet-key="conditions" title="New or used" />
				</template>

				<template v-if="isBuyUsed">
					<MakeModelFacet />

					<YearFacet />

					<OdometerFacet />

					<PriceUsedFacet />

					<StateFacet />
				</template>

				<CheckboxFacet
					facet-key="bodyTypes"
					title="Body type"
					accordion-closed-label="Show all body type"
					accordion-opened-label="Show less body type"
				/>

				<CheckboxFacet
					facet-key="features"
					title="Features"
					:display-first-labels="[
						'Apple Carplay',
						'Cruise Control',
						'Keyless Entry',
						'Leather Seats',
						'Parking Assist',
					]"
					accordion-closed-label="Show all features"
					accordion-opened-label="Show less features"
				/>

				<ClientOnly>
					<SlideDownAccordion
						:disclosure-button-at-bottom="true"
						class="w-full"
					>
						<template #disclosure-panel>
							<div class="mb-6 space-y-6">
								<YearFacet v-if="isSubscription" />

								<CheckboxFacet
									facet-key="transmissions"
									title="Transmissions"
									accordion-closed-label="Show all transmissions"
									accordion-opened-label="Show less transmissions"
								/>

								<CheckboxFacet
									facet-key="fuelTypes"
									title="Fuel type"
									accordion-closed-label="Show all fuel types"
									accordion-opened-label="Show less fuel types"
								/>

								<CheckboxFacet
									facet-key="driveTypes"
									title="Drive type"
									accordion-closed-label="Show all drive types"
									accordion-opened-label="Show less drive types"
								/>

								<CheckboxFacet
									facet-key="doors"
									title="Doors"
									accordion-closed-label="Show all doors"
									accordion-opened-label="Show less doors"
								/>

								<CheckboxFacet
									facet-key="seats"
									title="Seats"
									accordion-closed-label="Show all seats"
									accordion-opened-label="Show less seats"
								/>
							</div>
						</template>

						<template #disclosure-button="{ open }">
							<div
								class="flex w-full border-b border-gray-625 py-3"
								:class="[open ? '' : 'border-t']"
							>
								<div class="flex flex-1 flex-col text-left">
									<span class="font-bold text-gray-650">
										{{
											open
												? `Less filters`
												: `More filters`
										}}
									</span>
									<span
										v-if="!open"
										class="text-xsm text-gray-650"
									>
										Year, transmission and more
									</span>
								</div>
								<div class="flex items-center justify-center">
									<div
										class="caret"
										:class="open ? 'open' : ''"
									>
										<div
											class="on-open-rotate-180 ml-1 font-medium text-gray-600 duration-200"
										>
											<IconArrowDown
												class="w-3 text-gray-500"
											/>
										</div>
									</div>
								</div>
							</div>
						</template>
					</SlideDownAccordion>
				</ClientOnly>
			</div>
		</div>

		<div
			class="fixed inset-x-0 bottom-0 flex w-full space-x-4 bg-white px-5 py-5 lg:hidden"
			:class="[hasRemovableFilters ? 'justify-between' : 'justify-end']"
		>
			<div v-if="hasRemovableFilters" class="">
				<CarbarButton :size="4" :is-outline="true" @click="onClearAll">
					Clear all
				</CarbarButton>
			</div>
			<div class="flex">
				<CarbarButton
					v-if="pagination.total !== null"
					:size="4"
					:background-color="'bg-blue-400'"
					@click="closeMobileFilter"
				>
					Show me {{ pagination.total }} cars
				</CarbarButton>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue'
import { cloneDeep as _cloneDeep } from 'lodash-es'
import CheckboxFacet from '@/components/product-catalog/facets/CheckboxFacet.vue'
import MakeModelFacet from '@/components/product-catalog/facets/MakeModelFacet.vue'
import OdometerFacet from '@/components/product-catalog/facets/OdometerFacet.vue'
import PriceFacet from '@/components/product-catalog/facets/PriceFacet.vue'
import PriceUsedFacet from '@/components/product-catalog/facets/PriceUsedFacet.vue'
import StateFacet from '@/components/product-catalog/facets/StateFacet.vue'
import YearFacet from '@/components/product-catalog/facets/YearFacet.vue'
import SlideDownAccordion from '@/components/accordions/SlideDownAccordion.vue'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import { useProductCatalog } from '@/stores/product-catalog'
import IconFilter from '@/assets/svg/product-catalog/ico-filter.svg'
import IconTimes from '@/assets/svg/common/ico-times.svg'
import IconArrowDown from '@/assets/svg/common/ico-arrow_down.svg'

const productCatalog = useProductCatalog()

const props = defineProps({
	pageType: {
		type: String,
		default: 'subscribe',
	},
})

const activeFilters = computed(() => productCatalog.activeFilters)

const pagination = computed(() => productCatalog.productsPagination)

const showMobileFilter = computed(() => productCatalog.showMobileFilter)

const hasRemovableFilters = computed(() => {
	const currentActiveFilters = _cloneDeep(activeFilters.value)
	if (currentActiveFilters.sortBy) {
		delete currentActiveFilters.sortBy
	}

	return Object.keys(currentActiveFilters).length > 0
})

const closeMobileFilter = () => {
	productCatalog.showMobileFilter = false
	window.zE?.show()
}

const onClearAll = () => {
	productCatalog.clearFilter()
}

const isSubscription = computed(() => props.pageType === 'subscribe')
const isBuyUsed = computed(() => props.pageType === 'used')
</script>
